import { NoxusAlert } from "@/components/UI/NoxusAlert";
import React, { useRef, useState } from "react";
import { Button } from "@/components/shadcn/button";
import { Label } from "@/components/shadcn/label";
import { Switch } from "@/components/shadcn/switch";
import { Plus, X } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { VariableInput } from "./StepCard";

interface SmartSelector {
  primary?: string | null;
  fallbacks?: string[];
  description?: string;
  must_be_visible?: boolean;
  // Playwright get_by_* methods
  role?: string | null;
  role_name?: string | null;
  placeholder?: string | null;
  text?: string | null;
  label?: string | null;
  test_id?: string | null;
  title?: string | null;
  exact?: boolean | null;
  regex?: boolean;
  nth?: number | string | null;
}

interface SelectorEditorProps {
  selector: SmartSelector;
  onChange: (selector: SmartSelector) => void;
  /** Variable names available to the Nth field's `+` picker. Threaded
   * down from the enclosing StepCard so loop iteration variables (e.g.
   * `loop_index`) and playbook inputs are offered consistently. */
  variableNames?: string[];
}

export function SelectorEditor({
  selector,
  onChange,
  variableNames = [],
}: SelectorEditorProps) {
  // Fallbacks are plain strings in the model; rows need stable keys because
  // each one owns picker state (BindableInput) that must not jump rows when
  // an earlier fallback is deleted.
  const fallbackIdsRef = useRef<string[]>([]);
  const fallbacks = selector.fallbacks || [];
  while (fallbackIdsRef.current.length < fallbacks.length) {
    fallbackIdsRef.current.push(crypto.randomUUID());
  }
  if (fallbackIdsRef.current.length > fallbacks.length) {
    fallbackIdsRef.current.length = fallbacks.length;
  }

  // Determine current mode
  const hasPlaywrightMethod =
    selector.role != null ||
    selector.placeholder != null ||
    selector.text != null ||
    selector.label != null ||
    selector.test_id != null ||
    selector.title != null;
  const [mode, setMode] = useState<"css" | "playwright">(
    hasPlaywrightMethod || !selector.primary ? "playwright" : "css"
  );

  const handleModeChange = (newMode: "css" | "playwright") => {
    setMode(newMode);
    if (newMode === "playwright") {
      onChange({
        ...selector,
        primary: null,
        fallbacks: [],
      });
    } else {
      onChange({
        ...selector,
        role: null,
        role_name: null,
        placeholder: null,
        text: null,
        label: null,
        test_id: null,
        title: null,
        exact: null,
      });
    }
  };

  const handlePrimaryChange = (primary: string) => {
    onChange({ ...selector, primary });
  };

  const handleVisibilityChange = (must_be_visible: boolean) => {
    onChange({ ...selector, must_be_visible });
  };

  const handleAddFallback = () => {
    fallbackIdsRef.current.push(crypto.randomUUID());
    onChange({ ...selector, fallbacks: [...(selector.fallbacks || []), ""] });
  };

  const handleRemoveFallback = (index: number) => {
    const fallbacks = [...(selector.fallbacks || [])];
    fallbacks.splice(index, 1);
    fallbackIdsRef.current.splice(index, 1);
    onChange({ ...selector, fallbacks });
  };

  const handleFallbackChange = (index: number, value: string) => {
    const fallbacks = [...(selector.fallbacks || [])];
    fallbacks[index] = value;
    onChange({ ...selector, fallbacks });
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg bg-background">
      {/* Selector Mode Toggle */}
      <div className="flex flex-col gap-1.5">
        <Label>Selector Type</Label>
        <Select
          value={mode}
          onValueChange={(value) =>
            handleModeChange(value as "css" | "playwright")
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="css">CSS Selector</SelectItem>
            <SelectItem value="playwright">Playwright Locator</SelectItem>
          </SelectContent>
        </Select>
        {mode === "css" && (
          <>
            <NoxusAlert
              look="banner"
              variant="default"
              size="xs"
              title="Playwright locators are more robust than CSS selectors. They use accessible attributes like roles, labels, and ARIA properties."
            />
            <NoxusAlert
              look="banner"
              variant="default"
              size="xs"
              title="Add multiple selectors for resilience. The system will try them in order if the primary selector fails."
            />
          </>
        )}
      </div>

      {mode === "css" ? (
        <>
          {/* Primary Selector */}
          <div className="flex flex-col gap-1.5">
            <Label>Primary Selector</Label>
            <VariableInput
              value={selector.primary || ""}
              onChange={handlePrimaryChange}
              placeholder="#submit-button, .login-form, [data-testid='login']"
              variableNames={variableNames}
            />
          </div>

          {/* Fallback Selectors */}
          <div className="flex flex-col gap-1.5">
            <Label>Fallback Selectors</Label>
            <div className="space-y-2">
              {(selector.fallbacks || []).map((fallback, index) => (
                <div key={fallbackIdsRef.current[index]} className="flex gap-2">
                  <div className="min-w-0 flex-1">
                    <VariableInput
                      value={fallback}
                      onChange={(value) => handleFallbackChange(index, value)}
                      placeholder="Fallback selector..."
                      variableNames={variableNames}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveFallback(index)}
                    className="h-8 w-8 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              <Button variant="outline" size="sm" onClick={handleAddFallback}>
                <Plus className="h-4 w-4 mr-1" />
                Add fallback selector
              </Button>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* Playwright Locator Methods */}
          <div className="space-y-4">
            <div className="flex flex-col gap-1.5">
              <Label>Method</Label>
              <Select
                value={
                  selector.role != null
                    ? "role"
                    : selector.placeholder != null
                      ? "placeholder"
                      : selector.text != null
                        ? "text"
                        : selector.label != null
                          ? "label"
                          : selector.test_id != null
                            ? "test_id"
                            : selector.title != null
                              ? "title"
                              : ""
                }
                onValueChange={(method) => {
                  onChange({
                    ...selector,
                    role: method === "role" ? "button" : null,
                    placeholder: method === "placeholder" ? "" : null,
                    text: method === "text" ? "" : null,
                    label: method === "label" ? "" : null,
                    test_id: method === "test_id" ? "" : null,
                    title: method === "title" ? "" : null,
                  });
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a method..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="role">By Role</SelectItem>
                  <SelectItem value="placeholder">By Placeholder</SelectItem>
                  <SelectItem value="text">By Text</SelectItem>
                  <SelectItem value="label">By Label</SelectItem>
                  <SelectItem value="test_id">By Test ID</SelectItem>
                  <SelectItem value="title">By Title</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Role-specific fields */}
            {selector.role !== null && selector.role !== undefined && (
              <>
                <div className="flex flex-col gap-1.5">
                  <Label>Role</Label>
                  <VariableInput
                    value={selector.role || ""}
                    onChange={(role) => onChange({ ...selector, role })}
                    variableNames={variableNames}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label>Name (optional)</Label>
                  <VariableInput
                    value={selector.role_name || ""}
                    onChange={(role_name) =>
                      onChange({ ...selector, role_name: role_name || null })
                    }
                    variableNames={variableNames}
                  />
                </div>
              </>
            )}

            {/* Placeholder field */}
            {selector.placeholder !== null &&
              selector.placeholder !== undefined && (
                <div className="flex flex-col gap-1.5">
                  <Label>Placeholder Text</Label>
                  <VariableInput
                    value={selector.placeholder || ""}
                    onChange={(placeholder) =>
                      onChange({ ...selector, placeholder })
                    }
                    variableNames={variableNames}
                  />
                </div>
              )}

            {/* Text field */}
            {selector.text !== null && selector.text !== undefined && (
              <div className="flex flex-col gap-1.5">
                <Label>Text Content</Label>
                <VariableInput
                  value={selector.text || ""}
                  onChange={(text) => onChange({ ...selector, text })}
                  variableNames={variableNames}
                />
              </div>
            )}

            {/* Label field */}
            {selector.label !== null && selector.label !== undefined && (
              <div className="flex flex-col gap-1.5">
                <Label>Label Text</Label>
                <VariableInput
                  value={selector.label || ""}
                  onChange={(label) => onChange({ ...selector, label })}
                  variableNames={variableNames}
                />
              </div>
            )}

            {/* Test ID field */}
            {selector.test_id !== null && selector.test_id !== undefined && (
              <div className="flex flex-col gap-1.5">
                <Label>Test ID</Label>
                <VariableInput
                  value={selector.test_id || ""}
                  onChange={(test_id) => onChange({ ...selector, test_id })}
                  placeholder="login-button"
                  variableNames={variableNames}
                />
              </div>
            )}

            {/* Title field */}
            {selector.title !== null && selector.title !== undefined && (
              <div className="flex flex-col gap-1.5">
                <Label>Title (tooltip text)</Label>
                <VariableInput
                  value={selector.title || ""}
                  onChange={(title) => onChange({ ...selector, title })}
                  placeholder="Close"
                  variableNames={variableNames}
                />
              </div>
            )}

            {/* Exact match toggle — mutually exclusive with Regex
                pattern.  Turning either on auto-disables the other so
                the user never lands in a state where one is locked. */}
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label>Exact match</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Require exact text match (no partial matches)
                </p>
              </div>
              <Switch
                checked={selector.exact ?? false}
                onCheckedChange={(checked) =>
                  onChange({
                    ...selector,
                    exact: checked || null,
                    regex: checked ? false : selector.regex,
                  })
                }
              />
            </div>

            {/* Regex toggle */}
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label>Regex pattern</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Treat text fields as regex patterns
                </p>
              </div>
              <Switch
                checked={selector.regex ?? false}
                onCheckedChange={(checked) =>
                  onChange({
                    ...selector,
                    regex: checked || undefined,
                    exact: checked ? null : selector.exact,
                  })
                }
              />
            </div>
          </div>
        </>
      )}

      {/* Must be visible */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label>Element must be visible</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            Only interact with visible elements on the page
          </p>
        </div>
        <Switch
          checked={selector.must_be_visible ?? true}
          onCheckedChange={handleVisibilityChange}
        />
      </div>

      {/* Nth Element Index (optional) */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label>Nth Element</Label>
            <p className="text-xs text-muted-foreground mt-0.5">
              Target a specific element when multiple match
            </p>
          </div>
          <Switch
            checked={selector.nth != null && selector.nth !== ""}
            onCheckedChange={(checked) =>
              onChange({ ...selector, nth: checked ? 0 : null })
            }
          />
        </div>
        {selector.nth != null && selector.nth !== "" && (
          <VariableInput
            value={String(selector.nth ?? "")}
            onChange={(raw) => {
              // Empty → reset to 0 so the "enabled" Switch stays meaningful.
              if (raw === "") {
                onChange({ ...selector, nth: 0 });
                return;
              }
              // Integer input is stored as a number; anything else (a
              // :var[...] ref or `{{loop_index}}` template) stays a string —
              // SmartSelector.nth accepts `int | str`.  The floor lives in
              // the numeric input (min), not here, so the bound path and
              // the typed path behave the same.
              if (/^-?\d+$/.test(raw)) {
                onChange({ ...selector, nth: parseInt(raw, 10) });
              } else {
                onChange({ ...selector, nth: raw });
              }
            }}
            placeholder="e.g. 0, 1, or bind a variable"
            variableNames={variableNames}
            dataType="number"
            min={0}
          />
        )}
      </div>
    </div>
  );
}
